import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Search, ChevronUp, ChevronDown, ArrowUpDown,
  MessageSquare, Play, Phone, Filter, X
} from "lucide-react";
import { getActionSuggestion, PLAN_DATA } from "./demoConfig";

const STATUS_CONFIG = {
  upcoming: { label: "Upcoming", color: "#3B82F6", bg: "#EFF6FF" },
  demo_in_progress: { label: "In Progress", color: "#8B5CF6", bg: "#F5F3FF" },
  demo_done: { label: "Demo Done", color: "#F59E0B", bg: "#FFFBEB" },
  payment_sent: { label: "Payment Sent", color: "#F97316", bg: "#FFF7ED" },
  converted: { label: "Converted", color: "#10B981", bg: "#ECFDF5" },
  cold: { label: "Cold", color: "#6B7280", bg: "#F3F4F6" },
};

function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
}

function formatDateShort(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function GridView({ leads, auth, updateLead, onPreDemo, onWaChat, waChatLead }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [closerFilter, setCloserFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  const closers = useMemo(() => {
    const set = new Set(leads.map(l => l.closerName).filter(Boolean));
    return [...set].sort();
  }, [leads]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const filtered = useMemo(() => {
    let result = [...leads];

    // Search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(l =>
        (l.clientName || "").toLowerCase().includes(q) ||
        (l.clientLastName || "").toLowerCase().includes(q) ||
        (l.targetRole || "").toLowerCase().includes(q) ||
        (l.targetMarket || "").toLowerCase().includes(q) ||
        (l.whatsappNumber || "").includes(q) ||
        (l.closerName || "").toLowerCase().includes(q)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter(l => l.status === statusFilter);
    }

    // Closer filter
    if (closerFilter !== "all") {
      result = result.filter(l => l.closerName === closerFilter);
    }

    // Sort
    result.sort((a, b) => {
      let valA = a[sortKey];
      let valB = b[sortKey];

      // Handle nested values
      if (sortKey === "name") {
        valA = `${a.clientName} ${a.clientLastName || ""}`;
        valB = `${b.clientName} ${b.clientLastName || ""}`;
      }
      if (sortKey === "plan") {
        valA = a.recommendedPlan ? PLAN_DATA[a.recommendedPlan]?.priceNum || 0 : 0;
        valB = b.recommendedPlan ? PLAN_DATA[b.recommendedPlan]?.priceNum || 0 : 0;
      }
      if (sortKey === "daysFollowUp") {
        valA = a.demoDoneAt ? daysSince(a.demoDoneAt) : -1;
        valB = b.demoDoneAt ? daysSince(b.demoDoneAt) : -1;
      }

      if (valA == null) valA = "";
      if (valB == null) valB = "";

      if (typeof valA === "number" && typeof valB === "number") {
        return sortDir === "asc" ? valA - valB : valB - valA;
      }

      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      return sortDir === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });

    return result;
  }, [leads, search, statusFilter, closerFilter, sortKey, sortDir]);

  const activeFilters = (statusFilter !== "all" ? 1 : 0) + (closerFilter !== "all" ? 1 : 0);

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 grid-sort-inactive" />;
    return sortDir === "asc" ?
      <ChevronUp className="w-3 h-3 grid-sort-active" /> :
      <ChevronDown className="w-3 h-3 grid-sort-active" />;
  };

  return (
    <div className="grid-view" data-testid="grid-view">
      {/* Toolbar */}
      <div className="grid-toolbar" data-testid="grid-toolbar">
        <div className="grid-search">
          <Search className="w-4 h-4" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search leads..."
            className="grid-search-input"
            data-testid="grid-search-input"
          />
        </div>
        <div className="grid-toolbar-right">
          <Button
            size="sm"
            variant={showFilters ? "default" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
            className={`grid-filter-btn ${activeFilters > 0 ? "grid-filter-active" : ""}`}
            data-testid="grid-filter-btn"
          >
            <Filter className="w-3.5 h-3.5" />
            Filters{activeFilters > 0 ? ` (${activeFilters})` : ""}
          </Button>
          <span className="grid-count" data-testid="grid-count">
            {filtered.length} lead{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Filter bar */}
      {showFilters && (
        <div className="grid-filters" data-testid="grid-filters">
          <div className="grid-filter-group">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="grid-filter-select"
              data-testid="grid-status-filter"
            >
              <option value="all">All statuses</option>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>
          <div className="grid-filter-group">
            <label>Closer</label>
            <select
              value={closerFilter}
              onChange={(e) => setCloserFilter(e.target.value)}
              className="grid-filter-select"
              data-testid="grid-closer-filter"
            >
              <option value="all">All closers</option>
              {closers.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {activeFilters > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setStatusFilter("all"); setCloserFilter("all"); }}
              className="grid-clear-filters"
              data-testid="grid-clear-filters"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </Button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="grid-table-wrapper">
        <table className="grid-table" data-testid="grid-table">
          <thead>
            <tr>
              <th onClick={() => toggleSort("name")} className="grid-th-sortable">
                Lead <SortIcon col="name" />
              </th>
              <th onClick={() => toggleSort("targetRole")} className="grid-th-sortable">
                Role <SortIcon col="targetRole" />
              </th>
              <th onClick={() => toggleSort("targetMarket")} className="grid-th-sortable">
                Market <SortIcon col="targetMarket" />
              </th>
              <th onClick={() => toggleSort("status")} className="grid-th-sortable">
                Status <SortIcon col="status" />
              </th>
              <th onClick={() => toggleSort("closerName")} className="grid-th-sortable">
                Closer <SortIcon col="closerName" />
              </th>
              <th onClick={() => toggleSort("scheduledAt")} className="grid-th-sortable">
                Scheduled <SortIcon col="scheduledAt" />
              </th>
              <th onClick={() => toggleSort("plan")} className="grid-th-sortable">
                Plan <SortIcon col="plan" />
              </th>
              <th onClick={() => toggleSort("daysFollowUp")} className="grid-th-sortable">
                Follow-up <SortIcon col="daysFollowUp" />
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="grid-empty">
                  {search || statusFilter !== "all" || closerFilter !== "all"
                    ? "No leads match your filters"
                    : "No leads yet"}
                </td>
              </tr>
            ) : (
              filtered.map((lead) => {
                const statusCfg = STATUS_CONFIG[lead.status] || STATUS_CONFIG.cold;
                const plan = lead.recommendedPlan ? PLAN_DATA[lead.recommendedPlan] : null;
                const action = getActionSuggestion(lead);
                const days = daysSince(lead.demoDoneAt);

                return (
                  <tr key={lead.id} className="grid-row" data-testid={`grid-row-${lead.id}`}>
                    <td className="grid-cell-lead">
                      <span className="grid-lead-name grid-lead-name-link" onClick={() => navigate(`/demo/lead/${lead.id}`)} data-testid={`grid-lead-name-${lead.id}`}>
                        {lead.clientName} {lead.clientLastName || ""}
                      </span>
                      {lead.whatsappNumber && (
                        <span className="grid-lead-phone">
                          <Phone className="w-2.5 h-2.5" /> {lead.whatsappNumber}
                        </span>
                      )}
                    </td>
                    <td className="grid-cell-role">{lead.targetRole || "—"}</td>
                    <td className="grid-cell-market">{lead.targetMarket || "—"}</td>
                    <td>
                      <span
                        className="grid-status-badge"
                        style={{ color: statusCfg.color, background: statusCfg.bg }}
                        data-testid={`grid-status-${lead.id}`}
                      >
                        {statusCfg.label}
                      </span>
                    </td>
                    <td className="grid-cell-closer">{lead.closerName || "—"}</td>
                    <td className="grid-cell-date">{formatDateTime(lead.scheduledAt)}</td>
                    <td className="grid-cell-plan">
                      {plan ? (
                        <span className="grid-plan-badge">{plan.name} &middot; {plan.price}</span>
                      ) : "—"}
                    </td>
                    <td>
                      {action.text ? (
                        <span className={`grid-action-hint grid-action-${action.color}`}>
                          {action.text}
                        </span>
                      ) : (
                        <span className="grid-cell-muted">
                          {days !== null && days >= 0 ? `${days}d ago` : "—"}
                        </span>
                      )}
                    </td>
                    <td className="grid-cell-actions">
                      {lead.whatsappNumber && (
                        <button
                          className={`grid-action-btn grid-action-wa ${waChatLead?.id === lead.id ? "grid-action-wa-active" : ""}`}
                          onClick={() => onWaChat(waChatLead?.id === lead.id ? null : lead)}
                          title="WhatsApp"
                          data-testid={`grid-wa-${lead.id}`}
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        className="grid-action-btn grid-action-demo"
                        onClick={() => navigate(`/demo/live/${lead.id}`)}
                        title="Start demo"
                        data-testid={`grid-demo-${lead.id}`}
                      >
                        <Play className="w-3.5 h-3.5" />
                      </button>
                      <GridMoreMenu lead={lead} updateLead={updateLead} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GridMoreMenu({ lead, updateLead }) {
  const [open, setOpen] = useState(false);

  const actions = [];
  if (lead.status === "demo_done") {
    actions.push({ label: "Payment sent", fn: () => updateLead(lead.id, { status: "payment_sent", paymentSentAt: new Date().toISOString() }) });
    actions.push({ label: "Mark cold", fn: () => updateLead(lead.id, { status: "cold" }) });
  }
  if (lead.status === "payment_sent") {
    actions.push({ label: "Converted", fn: () => {
      const plan = lead.recommendedPlan ? PLAN_DATA[lead.recommendedPlan] : null;
      updateLead(lead.id, { status: "converted", convertedAt: new Date().toISOString(), convertedAmount: plan?.priceNum || 0 });
    }});
    actions.push({ label: "Mark cold", fn: () => updateLead(lead.id, { status: "cold" }) });
  }
  if (lead.status === "cold") {
    actions.push({ label: "Reopen", fn: () => updateLead(lead.id, { status: "demo_done" }) });
  }
  if (lead.status === "upcoming") {
    actions.push({ label: "Mark demo done", fn: () => updateLead(lead.id, { status: "demo_done", demoDoneAt: new Date().toISOString() }) });
    actions.push({ label: "Cancel", fn: () => updateLead(lead.id, { status: "cold" }) });
  }
  if (lead.status === "demo_in_progress") {
    actions.push({ label: "Mark demo done", fn: () => updateLead(lead.id, { status: "demo_done", demoDoneAt: new Date().toISOString() }) });
  }

  if (actions.length === 0) return null;

  return (
    <div className="grid-more-wrap" style={{ position: "relative" }}>
      <button
        className="grid-action-btn grid-action-more"
        onClick={() => setOpen(!open)}
        data-testid={`grid-more-${lead.id}`}
      >
        &middot;&middot;&middot;
      </button>
      {open && (
        <div className="grid-dropdown" onMouseLeave={() => setOpen(false)}>
          {actions.map((a, i) => (
            <button key={i} className="grid-dropdown-item" onClick={() => { a.fn(); setOpen(false); }}>
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
